/**
 * useSceneParams - 太阳码 scene 参数解析 composable
 *
 * 功能：
 * 1. 在页面 onLoad 时自动解析 scene 参数
 * 2. 如果 scene 是指纹码，调用接口获取业务参数
 * 3. 返回解析后的参数，对业务页面透明
 *
 * 使用方式：
 * ```typescript
 * import { useSceneParams } from '@/composables/useSceneParams'
 *
 * const { params, loading, parseScene } = useSceneParams()
 *
 * onLoad(async (options) => {
 *   // 解析 scene 参数，返回合并后的 options
 *   const mergedOptions = await parseScene(options)
 *   // 使用 mergedOptions 中的业务参数
 *   const societyId = mergedOptions.societyId
 * })
 * ```
 */

import { ref } from 'vue'
import sceneParser, {
  parseSceneOptions,
  getCachedBusinessParams,
} from '@/interceptors/sceneParser'

const { isFingerprintCode, parseFingerprintCode } = sceneParser

export function useSceneParams() {
  // 解析后的参数
  const params = ref<Record<string, string | undefined>>({})
  // 加载状态
  const loading = ref(false)
  // 错误信息
  const error = ref<string | null>(null)
  // 是否已解析
  const parsed = ref(false)

  /**
   * 解析 scene 参数
   * 如果 scene 是指纹码，会调用接口获取业务参数并合并到 options 中
   * 同时会将 promotionfingerprintId 写入页面 options，供推广系统使用
   *
   * @param options 页面 onLoad 的 options
   * @param pagePath 页面路径（可选，不传会自动获取）
   * @returns 合并后的 options
   */
  const parseScene = async (
    options: Record<string, string | undefined>,
    pagePath?: string
  ): Promise<Record<string, string | undefined>> => {
    loading.value = true
    error.value = null

    try {
      // 获取当前页面路径
      let currentPath = pagePath
      if (!currentPath) {
        const pages = getCurrentPages()
        const currentPage = pages[pages.length - 1]
        currentPath = currentPage?.route || ''
      }

      // 调用解析方法
      const mergedOptions = await parseSceneOptions(options, currentPath)
      params.value = mergedOptions
      parsed.value = true

      // 将 promotionfingerprintId 写入页面 options，供推广系统（initFingerprint）使用
      // #ifdef MP-WEIXIN
      if (mergedOptions?.scene && typeof mergedOptions?.scene === 'string' && mergedOptions?.scene?.includes('PF')) {
        mergedOptions.promotionfingerprintId = mergedOptions.scene
      }
      if (mergedOptions.promotionfingerprintId) {
        const pages = getCurrentPages()
        const currentPage = pages[pages.length - 1] as any
        if (currentPage && currentPage.options) {
          currentPage.options.promotionfingerprintId = mergedOptions.promotionfingerprintId
        }
      }
      // #endif

      return mergedOptions
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : '解析 scene 参数失败'
      error.value = errorMsg
      console.error('[useSceneParams] 解析失败:', err)
      // 返回原始 options
      params.value = options
      return options
    } finally {
      loading.value = false
    }
  }

  /**
   * 快速解析 scene（同步检查，异步获取）
   * 先同步检查是否需要解析，如果需要则异步获取
   *
   * @param options 页面 onLoad 的 options
   * @returns 是否需要异步解析
   */
  const quickParse = (options: Record<string, string | undefined>): boolean => {
    const scene = options.scene
    if (!scene) {
      params.value = options
      parsed.value = true
      return false
    }

    let decodedScene = scene
    try {
      decodedScene = decodeURIComponent(scene)
    } catch {
      // 解码失败，使用原值
    }

    // 检查是否为指纹码
    if (!isFingerprintCode(decodedScene)) {
      params.value = options
      parsed.value = true
      return false
    }

    // 检查缓存
    const cached = getCachedBusinessParams(decodedScene)
    if (cached) {
      params.value = { ...cached, ...options }
      parsed.value = true
      return false
    }

    // 需要异步解析
    return true
  }

  /**
   * 获取单个参数值
   *
   * @param key 参数名
   * @param defaultValue 默认值
   * @returns 参数值
   */
  const getParam = (key: string, defaultValue = ''): string => {
    return params.value[key] || defaultValue
  }

  return {
    // 状态
    params,
    loading,
    error,
    parsed,
    // 方法
    parseScene,
    quickParse,
    getParam,
    // 工具方法
    isFingerprintCode,
    parseFingerprintCode,
  }
}

export default useSceneParams
